import { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';

import { useIsRefreshingSession } from '@/api/bff/auth/use-refresh-session';
import { useToast } from '@/shared/design-system';
import { GeneralLoadingFallback } from '@/shared/modules/general-loading-fallback';
import { useSelectedUserConfigSlice } from '@/stores/selectors';
import { UserRole } from '@/stores/slices/selected-user-config';

import { PagePaths } from './page-paths';
import { ROUTE_ACCESS_DENIED_MESSAGE } from './require-permission';

export type RequireSessionProps = PropsWithChildren<{
  /** Account session the route requires the user to have switched into. */
  session: UserRole | readonly UserRole[];
}>;

/**
 * Route-level session gate (GP-3897): renders `children` only while the active
 * account session matches `session`.
 *
 * Distinct from `RequirePermission`, and needed alongside it: a Gamma team
 * member keeps their gamma-plane permissions in every session, so a permission
 * check alone lets them reach the admin surfaces from their Personal Account by
 * typing the URL. The backend applies the same distinction — see
 * `ViewerTierResolver`, where the elevated tier requires `SessionType.Admin`
 * *and* the permission.
 *
 * `selectedUserRole` is persisted to localStorage, so it is already hydrated on
 * first paint and a refresh inside the admin session does not bounce.
 */
export const RequireSession: FC<RequireSessionProps> = ({ session, children }) => {
  const isRefreshingSession = useIsRefreshingSession();
  const { selectedUserRole } = useSelectedUserConfigSlice();
  const sessionDenied = Array.isArray(session)
    ? !session.includes(selectedUserRole)
    : selectedUserRole !== session;
  const isDenied = !isRefreshingSession && sessionDenied;
  const hasNotifiedRef = useRef(false);
  const { add: addToast } = useToast();

  useEffect(() => {
    if (!isDenied || hasNotifiedRef.current) return;

    hasNotifiedRef.current = true;
    addToast({ title: ROUTE_ACCESS_DENIED_MESSAGE, data: { status: 'error' } });
  }, [isDenied, addToast]);

  if (!sessionDenied) return children;

  if (isRefreshingSession) return <GeneralLoadingFallback />;

  if (isDenied) {
    return <Navigate to={PagePaths.root} replace />;
  }

  return children;
};
